/**
 * ProductCard — Product thumbnail with image, heart, shop, title, price
 *
 * Used for: horizontal product scroll on home/browse screens
 */
import type { ReactNode } from 'react'
import { Image } from 'react-native'
import { styled, XStack, YStack, Text as TamaguiText, type GetProps } from 'tamagui'
import { FavoriteButton } from '../elements/FavoriteButton'

const ProductCardFrame = styled(YStack, {
  name: 'ProductCard',
  w: 165,
  bg: '$backgroundCard',
  br: '$lg',
  overflow: 'hidden',
  cursor: 'pointer',
  userSelect: 'none',
  animation: 'quick',
  animateOnly: ['opacity', 'transform'],
  pressStyle: { opacity: 0.85, scale: 0.97 },
  hoverStyle: { opacity: 0.92 },
})

const ProductImageWrap = styled(YStack, {
  name: 'ProductImageWrap',
  w: '100%',
  h: 190,
  position: 'relative',
  bg: 'rgb(224, 226, 219)',
})

const HeartWrap = styled(XStack, {
  name: 'HeartWrap',
  position: 'absolute',
  top: 8,
  right: 8,
  w: 34,
  h: 34,
  br: '$full',
  bg: 'rgba(255,255,255,0.9)',
  ai: 'center',
  jc: 'center',
})

const ProductInfo = styled(YStack, {
  name: 'ProductInfo',
  p: 10,
  gap: 2,
})

const ShopRow = styled(XStack, {
  name: 'ShopRow',
  ai: 'center',
  gap: 4,
})

const ShopName = styled(TamaguiText, {
  name: 'ShopName',
  fontFamily: '$body',
  fontSize: 11,
  color: '$colorSecondary',
})

const ProductName = styled(TamaguiText, {
  name: 'ProductName',
  fontFamily: '$body',
  fontSize: 13,
  fontWeight: '600',
  color: '$color',
  numberOfLines: 1,
})

const ProductPrice = styled(TamaguiText, {
  name: 'ProductPrice',
  fontFamily: '$body',
  fontSize: 14,
  fontWeight: '700',
  color: '$colorPrimary',
  mt: 2,
})

type ProductCardFrameProps = GetProps<typeof ProductCardFrame>

interface ProductCardProps extends ProductCardFrameProps {
  imageUri?: string | null
  title: string
  price: string
  shopName?: string
  shopIcon?: ReactNode
  /** Whether this product is favorited — renders animated FavoriteButton when provided */
  isFavorited?: boolean
  imagePlaceholder?: ReactNode
  onPress?: () => void
  onHeartPress?: () => void
}

export const ProductCard = ProductCardFrame.styleable<ProductCardProps>(
  ({ imageUri, title, price, shopName, shopIcon, isFavorited, imagePlaceholder, onPress, onHeartPress, ...props }, ref) => {
    return (
      <ProductCardFrame ref={ref} onPress={onPress} {...props}>
        <ProductImageWrap>
          {imageUri ? (
            <Image
              source={{ uri: imageUri }}
              style={{ width: '100%', height: '100%' }}
              resizeMode="cover"
            />
          ) : (
            <XStack f={1} ai="center" jc="center">
              {imagePlaceholder}
            </XStack>
          )}
          {isFavorited !== undefined && (
            <HeartWrap>
              <FavoriteButton
                active={isFavorited}
                onPress={(e: any) => { e?.stopPropagation?.(); onHeartPress?.() }}
                size={18}
              />
            </HeartWrap>
          )}
        </ProductImageWrap>
        <ProductInfo>
          {shopName && (
            <ShopRow>
              {shopIcon}
              <ShopName>{shopName}</ShopName>
            </ShopRow>
          )}
          <ProductName>{title}</ProductName>
          <ProductPrice>{price}</ProductPrice>
        </ProductInfo>
      </ProductCardFrame>
    )
  },
)
